import {Generator} from "@/lib/helper";
import BubbleService from "../BubbleService";
import {Application, ApplicationSchedule} from "@/lib/types/finsey/application";

class LoanService extends BubbleService {
    constructor() {
        super();
    }

    getLoanList = (phoneNumber: string) => {
        return this.postWf("/loan_management", {phone_number: phoneNumber});
    };

    getOpenLoanList = (loans: any[]) => {
        let openLoans = loans.filter((item: { Status: string; }) => {
            return item.Status != "Kết thúc";
        });
        return openLoans;
    };

    getCloseLoanList = (loans: any[]) => {
        let closeLoans = loans.filter((item: { Status: string; }) => {
            return item.Status == "Kết thúc";
        });
        return [];
    };

    getLoanDetail = async (applicationId: string) => {
        const data: any = await this.postWf("/application_retrieve_loan_management", {
            application_id: applicationId,
        });
        return data.schedule_payment;
    };

    createApplication = (data: {
        school: any;
        schoolType?: any;
        semester?: any;
        admissionMethod?: any;
        amount?: any;
        month?: any;
        name: any;
        student_id: any;
        phoneNumber?: any;
        email: any;
        studentAccount?: any;
        studentPassword?: any;
        cosignerName?: any;
        cosignerRelationship?: any;
        cosignerPhoneNumber?: any;
        phone_number?: any;
        application_id?: any;
        code?: any;
        contract_code?: any;
        principle_amount?: any;
        id_code?: any;
        payment_term?: any;
        student_account?: any;
        student_password?: any;
        cosigner_phone?: any;
        cosigner_name?: any;
        cosigner_relationship?: any;
        monthly_amount?: any;
        interest_amount?: any;
        service_fee?: any;
        isBadDebt?: any;
        referral?: any;
        coupon?: any;
        address?: any;
        birthday?: any;
        birthplace?: any;
        district?: any;
        expiry?: any;
        ward?: any;
        province?: any;
        issue_date?: any;
        issue_by?: any;
        nationality?: any;
        gender?: any;
        content?: any;
        description?: any;
        image?: any;
        raw?: any;
        slug?: any;
        title?: any;
        isSpecial?: any;
        category?: any;
        author?: any;
        supports?: string | undefined;
    }) => {
        let {
            school,
            schoolType,
            semester,
            admissionMethod,
            amount,
            month,
            name,
            student_id,
            phoneNumber,
            email,
            studentAccount,
            studentPassword,
            cosignerName,
            cosignerRelationship,
            cosignerPhoneNumber,
        } = data;
        let formData = {
            name,
            school,
            student_id,
            amount,
            phone_number: phoneNumber,
            payment_term: month,
            student_account: studentAccount,
            student_password: studentPassword,
            cosigner_phone: cosignerPhoneNumber,
            cosigner_name: cosignerName,
            cosigner_relationship: cosignerRelationship,
        };
        return this.postWf("zalo_recieve_form", data);
    };

    updateScheduleLoanStatus = async (code: any, application_id: any) => {
        const data: any = await this.postWf('update_schedule_payment_status', {code, application_id})
        return data.scheduled_payment
    }

    getDiscount = async (coupon: any) => {
        let constraint: any = [{key: 'coupon', constraint_type: 'equals', value: coupon || ''}]
        constraint = JSON.stringify(constraint)
        const data: any = await this.getObj(`Promotion?constraints=${constraint}`)
        return data.results[0];

    }

    getRate = async (isReapply: boolean = false) => {
        let data: any = await this.getWf("rate");
        if(isReapply){
            data.interest_rate = data.interest_rate.filter((item: any) => {
                return item.type == 'reapply'
            })
        }
        return data;
    };

    private getRateByMonth = (month: any, rates: { interest_rate: any[]; service_rate: { Value: any; }; }, discount?: {
        discount: number;
    }) => {
        let interest_rate = rates.interest_rate?.find((item: { Duration: any; }) => {
            return item.Duration == month;
        });
        let service_rate = rates.service_rate?.Value;
        let discountValue = discount && discount.discount ? discount.discount / 100 : 0
        interest_rate = interest_rate?.Value - discountValue
        return {interest_rate, service_rate};
    }

    private calculatePMT = (amount: number, month: number, interest_rate: number) => {
        if (interest_rate == 0) {
            // If interest rate is 0, the payment is just the loan amount divided by months
            return amount / month;
        }
        interest_rate = interest_rate / 12;
        let result =
            (interest_rate * amount) /
            (1 - Math.pow(1 + interest_rate, -month));
        return Math.round(result);
    };

    private calculateServiceFee = (amount: number, month: number, service_rate: number) => {
        let result = (amount * service_rate) / month;
        return Math.round(result);
    };

    private totalAmountPerMonth = (amount: number, month: number, rates: any, discount?: any) => {
        let rate = this.getRateByMonth(month, rates, discount);
        let pmt = this.calculatePMT(amount, month, rate.interest_rate);
        let serviceFee = this.calculateServiceFee(amount, month, rate.service_rate);
        let result = pmt + serviceFee;

        //Làm tròn số thập phân hoặc làm tròn 1000
        result = Math.round(result);
        // result = Math.ceil(result / 1000) * 1000

        return {pmt, serviceFee, total: result};
    };

    loanAmountIinfo = (amount: number, month: number, rates: {
        interest_rate: { Value: any; Duration: number; }[];
        service_rate: { Value: any; };
    }, discount?: any) => {
        //Caculate with discount
        let totalAmountPermonth = this.totalAmountPerMonth(amount, month, rates, discount)
        let totalPayPerMonth = totalAmountPermonth.total
        let total = totalAmountPermonth.total * month
        let baseTotal = amount
        let serviceFee = totalAmountPermonth.serviceFee * month
        let interestTotal = total - baseTotal - serviceFee
        let payStartDate = new Date()
        payStartDate.setDate(payStartDate.getDate() + 7)
        payStartDate.setMonth(payStartDate.getMonth() + 1)

        //Calculate without discount
        let totalAmounthPerMonthWithoutDiscount = this.totalAmountPerMonth(amount, month, rates)
        let totalWithouDiscount = totalAmounthPerMonthWithoutDiscount.total * month
        let interestTotalWithouDiscount = totalWithouDiscount - amount - serviceFee

        let result = {
            totalPayPerMonth,
            total,
            baseTotal,
            serviceFee,
            interestTotal,
            payStartDate,
            interestTotalWithoutDiscount: interestTotalWithouDiscount,
            discountAmount: totalWithouDiscount - total
        }
        return result
    }

    calculateSchedulePerMonth = (amount: number, month: number, rates: any, discount?: any) => {
        let result: any[] = []

        let rate = this.getRateByMonth(month, rates, discount)
        let interest_rate = rate.interest_rate
        let pmt = this.calculatePMT(amount, month, interest_rate);
        pmt = Math.ceil(pmt / 1000) * 1000

        for (let i = 1; i <= month; i++) {
            let interest_rate_paid = Math.ceil(amount * interest_rate / 12);
            let principle_paid = Math.ceil(pmt - interest_rate_paid)
            let outstanding_principle = Math.ceil(amount - principle_paid)
            let schedule = {month: i, outstanding_principle, interest_rate_paid, principle_paid}
            result = [...result, schedule]
            amount = outstanding_principle;
        }
        return result
    }

    calculateEarlyPayment = (application: Application, schedule: ApplicationSchedule[]) => {
        let remainTotalPaymentAmount = 0;
        let remainTotalPrincipleAmount = 0;
        let count = 0;
        schedule.forEach((item, index) => {
            if (item.Paid_status != 'Đã thanh toán đủ') {
                console.log(item.Principle_amount,item.Late_payment, item.Payment_amount)
                remainTotalPrincipleAmount += item.Principle_amount + item.Late_payment
                remainTotalPaymentAmount += item.Payment_amount
                count ++
            }
        })

        const earlyPaymentAmount = Math.round(remainTotalPrincipleAmount * 1.03);
        console.log({remainTotalPaymentAmount, remainTotalPrincipleAmount, count})
        return {remainTotalPaymentAmount, earlyPaymentAmount}
    }

    oldEarlyCalculate = (application: Application, schedule: ApplicationSchedule[]) => {
        //Các tháng trễ hạn
        let passiveAmount: number = 0

        //Tháng đến hạn
        let activeAmount: number = 0

        //Các tháng chưa đến hạn
        let inactiveSchedules: ApplicationSchedule[] | [] = []

        let prevPrincipleAmount = 0

        schedule.forEach((item, index) => {

            if (
                item.Paid_status == 'Đến hạn thanh toán' ||
                item.Paid_status == 'Thanh toán trễ' ||
                item.Paid_status == 'Đã thanh toán đủ'
            ) {
                prevPrincipleAmount += item.Principle_amount
            }

            switch (item.Paid_status) {
                case 'Thanh toán trễ':
                    passiveAmount += item.Payment_amount + item.Excess_amount
                    break;
                case 'Đến hạn thanh toán':
                    activeAmount += item.Payment_amount
                    break;
                default:
                    break;
            }
        })
    }

    getCustomRates = (interestRate: number, serviceFee: number) => {
        let interest_rate = [
            {Value: interestRate, Duration: 4},
            {Value: interestRate, Duration: 6},
            {Value: interestRate, Duration: 8},
            {Value: interestRate, Duration: 10},
            {Value: interestRate, Duration: 12},
        ];

        let service_rate = {
            Value: serviceFee
        };

        return {interest_rate, service_rate};
    }

    calculateMomo = (amount: any, month: any) => {
        let rates = this.getCustomRates(0.746, 0)
        return this.loanAmountIinfo(amount, month, rates)
    }

    calculateFeCredit = (amount: any, month: any) => {
        let rates = this.getCustomRates(0.63, 0)
        return this.loanAmountIinfo(amount, month, rates)
    }

    calculateHomeCredit = (amount: any, month: any) => {
        let rates = this.getCustomRates(0.495, 0)
        return this.loanAmountIinfo(amount, month, rates)
    }

    getFormNotification = async () => {
        const data: any = await this.getObj('/Form_Notification');
        const generator = new Generator()
        const randomList = generator.randomList(data.results)
        let result: any[] = []
        for (let i = 0; i <= 49; i++) {
            result = [...result, randomList[i]]
        }
        return result
    }
}

export default LoanService;
